import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2,
  Lock,
  User,
  Eye,
  EyeOff,
  ArrowRight,
  CalendarDays,
  BookOpen,
  BarChart3,
  CreditCard,
  Phone,
  Globe,
  Sparkles,
  AlertCircle
} from "lucide-react";
import { TEST_IDS } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL}/api` : "/api";

const SLIDES = [
  {
    url: "https://images.unsplash.com/photo-1587271407850-8d438ca9fdf2?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    title: "Grand Convention & Wedding Halls",
    subtitle: "Manage pillarless banquet halls, royal mandap stages & luxury dining spaces"
  },
  {
    url: "https://images.unsplash.com/photo-1745573673416-66e829644ae9?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    title: "Royal Stage & Floral Celebrations",
    subtitle: "Real-time date availability, day/night slots & multi-day event scheduling"
  },
  {
    url: "https://images.unsplash.com/photo-1761110787206-2cc164e4913c?crop=entropy&cs=srgb&fm=jpg&w=1600&q=85",
    title: "Luxury Banquet & Dining Management",
    subtitle: "Track GST invoices, advances, labor & electricity charges in one place"
  }
];

const FEATURE_PILLS = [
  { label: "Bookings", icon: BookOpen, desc: "Instant booking schedule" },
  { label: "Calendar", icon: CalendarDays, desc: "Day & night slot availability" },
  { label: "Reports", icon: BarChart3, desc: "Revenue & GST tracking" },
  { label: "Payments", icon: CreditCard, desc: "Advances & balance dues" }
];

export default function LoginPage({ onCustomLogin }) {
  const [showPassword, setShowPassword] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Username & Password credentials state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmittingCredentials, setIsSubmittingCredentials] = useState(false);
  const [authError, setAuthError] = useState("");

  // Slideshow timer
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % SLIDES.length);
    }, 5500);
    return () => clearInterval(interval);
  }, []);

  const handleUsernamePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setAuthError("Please enter your User ID / Username and Password.");
      return;
    }

    setIsSubmittingCredentials(true);
    setAuthError("");

    try {
      const res = await axios.post(`${API}/auth/login`, {
        username: username.trim(),
        password: password
      });

      if (res.data.token) {
        if (onCustomLogin) {
          onCustomLogin(res.data.token);
        }
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || "Invalid User ID or Password. Please try again.";
      setAuthError(errorDetail);
    } finally {
      setIsSubmittingCredentials(false);
    }
  };

  return (
    <div
      data-testid={TEST_IDS.loginContainer}
      className="min-h-screen w-full bg-[#070b19] flex items-center justify-center p-3 sm:p-6 lg:p-10 font-sans"
    >
      {/* Main Two-Part Container */}
      <div className="w-full max-w-6xl min-h-[640px] bg-[#0c132c] rounded-3xl shadow-2xl border border-blue-900/30 overflow-hidden grid grid-cols-1 lg:grid-cols-12 animate-fade-in">
        
        {/* =========================================================
            LEFT SIDE: CONVENTION HALL PHOTO SLIDESHOW & FEATURES
           ========================================================= */}
        <div className="lg:col-span-7 relative min-h-[320px] sm:min-h-[420px] lg:min-h-[640px] overflow-hidden flex flex-col justify-between p-6 sm:p-8 lg:p-10 text-white">
          
          {SLIDES.map((slide, idx) => (
            <div
              key={idx}
              className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                idx === currentSlide ? "opacity-100 scale-100" : "opacity-0 scale-105 pointer-events-none"
              }`}
              style={{
                backgroundImage: `url(${slide.url})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            />
          ))}

          {/* Dark Gradient Overlay for Readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#070b19] via-[#0c132c]/70 to-[#070b19]/80" />

          {/* Top Brand Header on Photo Side */}
          <div className="relative z-10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center font-black text-white text-base shadow-lg shadow-orange-500/20">
                BD
              </div>
              <div>
                <span className="text-xs font-black tracking-widest uppercase text-amber-400 block">
                  Bharath Developers
                </span>
                <span className="text-lg font-black text-white tracking-tight">
                  Book<span className="text-orange-400">My</span>Events
                </span>
              </div>
            </div>

            {/* Slideshow Progress Dots */}
            <div className="flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-2.5 py-1 rounded-full border border-white/10">
              {SLIDES.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    idx === currentSlide ? "w-5 bg-orange-400" : "w-1.5 bg-white/40 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Middle Content: Title & Small Round Feature Icons Down the Side */}
          <div className="relative z-10 py-6 sm:py-8 space-y-5">
            <div className="space-y-2 max-w-lg">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-500/20 border border-orange-400/30 text-orange-300 text-[11px] font-bold">
                <Sparkles className="w-3 h-3 text-orange-400" />
                <span>Convention Hall Business Suite</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight leading-tight">
                {SLIDES[currentSlide].title}
              </h2>
              <p className="text-xs sm:text-sm text-gray-300 font-medium leading-relaxed">
                {SLIDES[currentSlide].subtitle}
              </p>
            </div>

            {/* Feature Icons Grid / Down the Side */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2">
              {FEATURE_PILLS.map((pill) => {
                const Icon = pill.icon;
                return (
                  <div
                    key={pill.label}
                    className="flex items-center gap-2 p-2.5 rounded-2xl bg-white/10 hover:bg-white/15 backdrop-blur-md border border-white/10 transition-all group"
                  >
                    <div className="w-8 h-8 rounded-xl bg-orange-500/20 border border-orange-400/30 flex items-center justify-center flex-shrink-0 group-hover:bg-orange-500 group-hover:text-white transition-colors">
                      <Icon className="w-4 h-4 text-orange-400 group-hover:text-white" />
                    </div>
                    <div className="min-w-0">
                      <span className="text-xs font-bold text-white block truncate">
                        {pill.label}
                      </span>
                      <span className="text-[10px] text-gray-300 block truncate">
                        {pill.desc}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Bottom Info: Contact Phone + Website Link */}
          <div className="relative z-10 pt-4 border-t border-white/10 flex flex-wrap items-center justify-between gap-3 text-xs">
            <a
              href="tel:+919241410275"
              className="flex items-center gap-2 text-gray-300 hover:text-white font-semibold transition-colors bg-black/30 backdrop-blur-sm px-3.5 py-1.5 rounded-xl border border-white/10"
            >
              <Phone className="w-3.5 h-3.5 text-orange-400" />
              <span>Contact: <strong className="text-white">+91 9241410275</strong></span>
            </a>

            <a
              href="https://www.bharathplantation.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-orange-400 hover:text-orange-300 font-bold transition-colors bg-black/30 backdrop-blur-sm px-3.5 py-1.5 rounded-xl border border-white/10"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>www.bharathplantation.com</span>
            </a>
          </div>
        </div>

        {/* =========================================================
            RIGHT SIDE: DARK NAVY LOGIN CARD
           ========================================================= */}
        <div className="lg:col-span-5 bg-[#0a1128] border-t lg:border-t-0 lg:border-l border-blue-900/40 p-6 sm:p-8 lg:p-10 flex flex-col justify-center text-white">
          
          <div className="max-w-md w-full mx-auto space-y-6">
            
            {/* Card Header: Business Name at the Top */}
            <div className="space-y-1.5 text-center sm:text-left">
              <div className="flex items-center justify-center sm:justify-start gap-2 text-orange-400">
                <Building2 className="w-5 h-5 text-orange-400" />
                <span className="text-xs font-black uppercase tracking-[0.2em]">
                  Bharath Developers
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                Owner Sign In
              </h1>
              <p className="text-xs text-gray-400 font-medium">
                Enter your User ID and password to access your convention hall portal.
              </p>
            </div>

            {authError && (
              <div className="p-3.5 rounded-2xl bg-rose-950/60 border border-rose-500/40 text-xs text-rose-200 font-medium flex items-center gap-2.5 animate-shake">
                <AlertCircle className="w-4 h-4 text-rose-400 flex-shrink-0" />
                <span>{authError}</span>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleUsernamePasswordSubmit} className="space-y-5">
              
              {/* Username Box */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1.5">
                  Username / User ID <span className="text-orange-400">*</span>
                </label>
                <div className="relative">
                  <User className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    data-testid="input-username"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="e.g. bookmyevents or owner@bookmyevents.com"
                    className="w-full pl-10 pr-4 py-3 rounded-2xl bg-[#121c3d] border border-blue-900/60 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30 text-xs sm:text-sm text-white placeholder-gray-500 transition-all font-medium"
                  />
                </div>
              </div>

              {/* Password Box with Eye Icon */}
              <div>
                <label className="block text-xs font-bold text-gray-300 mb-1.5">
                  Password <span className="text-orange-400">*</span>
                </label>
                <div className="relative">
                  <Lock className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type={showPassword ? "text" : "password"}
                    data-testid="input-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-10 pr-11 py-3 rounded-2xl bg-[#121c3d] border border-blue-900/60 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/30 text-xs sm:text-sm text-white placeholder-gray-500 transition-all font-medium"
                  />
                  <button
                    type="button"
                    data-testid="toggle-password-visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400 transition-colors p-1"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Wide Orange Login Button with Rounded Ends */}
              <button
                data-testid="btn-submit-credentials"
                type="submit"
                disabled={isSubmittingCredentials}
                className="w-full py-3.5 px-6 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-slate-950 font-black text-sm uppercase tracking-wider shadow-lg shadow-orange-500/25 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <span>{isSubmittingCredentials ? "Signing In..." : "Log In"}</span>
                <ArrowRight className="w-4 h-4 text-slate-950" />
              </button>
            </form>

            {/* Bottom Security Note */}
            <div className="pt-4 text-center text-[11px] text-gray-500">
              Protected by Bharath Developers • Enterprise Event Management System
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
